import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Target, Eye, Heart, Users, Settings, Sparkles, Search, FlaskConical, Rocket, MonitorCheck, ShieldCheck, GitBranch, Activity } from 'lucide-react';
import { Button } from '@/components/ui/button';

const ABOUT_IMG = "/images/about-ai-team.webp";

// Team roles defined inline in JSX below

const values = [
  { icon: Target, title: 'Precision', desc: 'We deliver targeted solutions that address specific business challenges, not generic AI implementations.' },
  { icon: Eye, title: 'Transparency', desc: 'We believe in explainable AI and clear communication throughout every engagement.' },
  { icon: Heart, title: 'Impact', desc: 'Every initiative we undertake is measured by tangible business outcomes and lasting value creation.' },
];

export default function AboutPage() {
  return (
    <div>
      {/* Hero */}
      <section data-testid="about-hero" className="py-24 md:py-32 bg-secondary">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <p className="text-xs uppercase tracking-[0.2em] font-bold text-teal mb-4">About Us</p>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-medium text-navy leading-[1.1] mb-6">
            Making AI Practical<br />for Business Operations
          </h1>
          <p className="text-base md:text-lg text-navy/60 max-w-2xl leading-relaxed">
            S Cube AI Consulting & Solutions helps organizations identify useful AI opportunities, build practical solutions, and deploy responsible systems that improve day-to-day operations.
          </p>
        </div>
      </section>

      {/* Mission */}
      <section data-testid="about-mission" className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div>
              <div className="aspect-[4/3] rounded-sm overflow-hidden">
                <img src={ABOUT_IMG} alt="Our Team" className="w-full h-full object-cover" />
              </div>
            </div>
            <div>
              <p className="text-xs uppercase tracking-[0.2em] font-bold text-accent-orange mb-4">Our Mission</p>
              <h2 className="font-heading text-3xl md:text-4xl font-medium text-navy mb-6">
                Turning AI Potential into Operational Value
              </h2>
              <p className="text-navy/60 leading-relaxed mb-6">
                We believe AI should start with a business problem, not a technology trend. Our approach connects strategy, data, process understanding, and responsible implementation so that AI investments are easier to adopt and easier to measure.
              </p>
              <p className="text-navy/60 leading-relaxed mb-8">
                We work across domains such as retail, manufacturing, energy, and telecom, helping teams move from early use-case discovery to production-ready solutions and operational adoption.
              </p>
              <div className="grid grid-cols-3 gap-6">
                {[
                  { val: 'Business-first', label: 'AI Strategy' },
                  { val: 'Practical', label: 'Solution Build' },
                  { val: 'Responsible', label: 'Deployment' },
                ].map(s => (
                  <div key={s.label}>
                    <p className="font-heading text-xl md:text-2xl font-medium text-teal">{s.val}</p>
                    <p className="text-xs text-navy/50 mt-1">{s.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section data-testid="about-values" className="py-24 md:py-32 bg-secondary">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="mb-16">
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-teal mb-4">Our Values</p>
            <h2 className="font-heading text-3xl md:text-4xl font-medium text-navy">
              What Drives Us
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {values.map((v, i) => {
              const Icon = v.icon;
              return (
                <div key={v.title} data-testid={`value-card-${i}`} className="p-8 md:p-12 bg-white border border-border rounded-sm">
                  <div className="w-12 h-12 rounded-sm bg-teal/10 flex items-center justify-center mb-6">
                    <Icon className="h-6 w-6 text-teal" />
                  </div>
                  <h3 className="font-heading text-xl font-medium text-navy mb-3">{v.title}</h3>
                  <p className="text-sm text-navy/60 leading-relaxed">{v.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Us */}
      <section data-testid="about-why-us" className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-teal mb-4">The S Cube Advantage</p>
            <h2 className="font-heading text-3xl md:text-4xl font-medium text-navy mb-4">
              Why Clients Choose Us
            </h2>
            <p className="text-navy/50 text-base max-w-2xl mx-auto">
              Our formula for delivering lasting impact combines three pillars: the right expertise, a clear process, and delivery accelerators that keep AI initiatives grounded.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8">
            {/* People */}
            <div data-testid="why-us-people" className="border border-border rounded-sm overflow-hidden">
              <div className="bg-accent-orange/10 px-8 py-6 flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-accent-orange/20 flex items-center justify-center flex-shrink-0">
                  <Users className="h-6 w-6 text-accent-orange" />
                </div>
                <h3 className="font-heading text-xl font-medium text-navy">People</h3>
              </div>
              <div className="p-8">
                <p className="text-sm text-navy/60 leading-relaxed mb-6">
                  Our multidisciplinary team delivers business outcomes through a blend of deep technical skill and hands-on industry experience.
                </p>
                <div className="space-y-2.5 mb-6">
                  {[
                    'Industry Experts',
                    'Domain Specialists',
                    'Solution Architects',
                    'Statisticians',
                    'Data Scientists',
                    'ML Engineers & many more',
                  ].map(role => (
                    <div key={role} className="flex items-center gap-2.5">
                      <div className="w-1.5 h-1.5 rounded-full bg-accent-orange flex-shrink-0" />
                      <span className="text-sm text-navy/70">{role}</span>
                    </div>
                  ))}
                </div>
                <div className="bg-secondary rounded-sm p-4">
                  <p className="text-xs text-navy/50 leading-relaxed">
                    Trained at ISI, IITs, IIMs & other leading institutes, our team brings state-of-the-art methods while experienced practitioners ensure every solution is pragmatic, interpretable, and aligned to operational SLAs.
                  </p>
                </div>
              </div>
            </div>

            {/* Process */}
            <div data-testid="why-us-process" className="border border-border rounded-sm overflow-hidden">
              <div className="bg-teal/10 px-8 py-6 flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-teal/20 flex items-center justify-center flex-shrink-0">
                  <Settings className="h-6 w-6 text-teal" />
                </div>
                <h3 className="font-heading text-xl font-medium text-navy">Process</h3>
              </div>
              <div className="p-8">
                <p className="text-sm text-navy/60 leading-relaxed mb-6">
                  A structured, battle-tested methodology that takes AI from ideation to sustained production impact.
                </p>
                <div className="space-y-4">
                  {[
                    { icon: Search, step: 'Discovery', desc: 'Understand business context, pain points, and data landscape to frame ML opportunities.' },
                    { icon: Target, step: 'Use-Case Prioritization', desc: 'Rank opportunities by feasibility, impact, and ROI to decide what to pursue first.' },
                    { icon: FlaskConical, step: 'Proof of Concept', desc: 'Build a focused prototype to validate technical viability and business value.' },
                    { icon: Rocket, step: 'Development & Deployment', desc: 'Engineer data pipelines, train models, and integrate into production systems.' },
                    { icon: MonitorCheck, step: 'Monitoring', desc: 'Track performance, detect drift, retrain models, and ensure sustained impact.' },
                  ].map(item => {
                    const StepIcon = item.icon;
                    return (
                      <div key={item.step} className="flex gap-3">
                        <div className="w-8 h-8 rounded-sm bg-teal/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <StepIcon className="h-4 w-4 text-teal" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-navy">{item.step}</p>
                          <p className="text-xs text-navy/50 leading-relaxed">{item.desc}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Accelerators */}
            <div data-testid="why-us-accelerators" className="border border-border rounded-sm overflow-hidden">
              <div className="bg-navy/5 px-8 py-6 flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-navy/10 flex items-center justify-center flex-shrink-0">
                  <Sparkles className="h-6 w-6 text-navy" />
                </div>
                <h3 className="font-heading text-xl font-medium text-navy">Accelerators</h3>
              </div>
              <div className="p-8">
                <p className="text-sm text-navy/60 leading-relaxed mb-6">
                  A compact suite of tools that embeds responsibility and quality into every stage of the ML lifecycle.
                </p>
                <div className="space-y-4">
                  {[
                    { icon: ShieldCheck, title: 'Responsible-by-Design Framing', desc: 'Ethical standards embedded into the foundations of every solution design.' },
                    { icon: Sparkles, title: 'Best-Practice Model Development', desc: 'Standardized development processes that ensure consistent, high-quality outputs.' },
                    { icon: ShieldCheck, title: 'Ethics-Approved Feature Sets', desc: 'Modeling features are assessed for fairness, appropriateness, and documented risks.' },
                    { icon: GitBranch, title: 'Causal Linkage Analysis', desc: 'Causal methods pinpoint the true impacting levers -- not just correlations.' },
                    { icon: Activity, title: 'Real-Time Model Governance', desc: 'Live performance monitoring detects deterioration early and triggers retraining.' },
                  ].map(item => {
                    const ItemIcon = item.icon;
                    return (
                      <div key={item.title} className="flex gap-3">
                        <div className="w-8 h-8 rounded-sm bg-navy/5 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <ItemIcon className="h-4 w-4 text-navy/60" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-navy">{item.title}</p>
                          <p className="text-xs text-navy/50 leading-relaxed">{item.desc}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32 bg-navy text-white text-center">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <h2 className="font-heading text-3xl md:text-4xl font-medium mb-6">
            Ready to Discuss an AI Opportunity?
          </h2>
          <p className="text-white/60 max-w-xl mx-auto mb-10">
            Share the operational challenge you want to solve, and we can help identify whether AI is the right path forward.
          </p>
          <Link to="/contact">
            <Button className="bg-teal text-white hover:bg-teal/90 rounded-full px-8 py-3 text-base h-auto">
              Get in Touch <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
